import React, { useEffect, useCallback, } from "react";

// --- TIPAGENS ---
export type DrawerAnchor = "left" | "right" | "top" | "bottom";
export type DrawerVariant = "permanent" | "persistent" | "temporary";

export interface DrawerProps extends React.HTMLAttributes<HTMLDivElement> {
  anchor?: DrawerAnchor;
  open?: boolean;
  onClose?: (event: React.MouseEvent | React.KeyboardEvent) => void;
  variant?: DrawerVariant;
  elevation?: number; // Representado de 0 a 24 no MUI, aqui mapeamos para as sombras do Tailwind
  children?: React.ReactNode;
}

// Utilitário para mapear a prop 'elevation' para sombras do Tailwind
const getElevationClass = (elevation: number) => {
  if (elevation === 0) return "shadow-none";
  if (elevation < 4) return "shadow-sm";
  if (elevation < 8) return "shadow-md";
  if (elevation < 16) return "shadow-lg";
  if (elevation < 20) return "shadow-xl";

  return "shadow-2xl";
};

export default function Drawer({
  anchor = "left",
  open = false,
  onClose,
  variant = "temporary",
  elevation = 16,
  children,
  className = "",
  ...props
}: DrawerProps): JSX.Element {
  const isTemporary = variant === "temporary";
  const isPermanent = variant === "permanent";

  // O permanent está sempre aberto ignorando a prop 'open'
  const isOpen = isPermanent || open;

  // Fecha o drawer ao pressionar a tecla Escape
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === "Escape" && isOpen && isTemporary && onClose) {
        onClose(event as unknown as React.KeyboardEvent);
      }
    },
    [isOpen, isTemporary, onClose]
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);

    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  // Bloqueia o scroll da página quando um Drawer temporário estiver aberto
  useEffect(() => {
    if (isTemporary && isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [isTemporary, isOpen]);

  // --- CLASSES DE POSICIONAMENTO E ANIMAÇÃO ---
  const anchorClasses = {
    left: "inset-y-0 left-0 w-72 h-full",
    right: "inset-y-0 right-0 w-72 h-full",
    top: "inset-x-0 top-0 w-full h-auto",
    bottom: "inset-x-0 bottom-0 w-full h-auto",
  }[anchor];

  const transformClosedClasses = {
    left: "-translate-x-full",
    right: "translate-x-full",
    top: "-translate-y-full",
    bottom: "translate-y-full",
  }[anchor];

  const transformOpenClasses = {
    left: "translate-x-0",
    right: "translate-x-0",
    top: "translate-y-0",
    bottom: "translate-y-0",
  }[anchor];

  // --- RENDERIZAÇÃO ---
  return (
    <>
      {/* BACKDROP (Apenas para variante temporary) */}
      {isTemporary && (
        <div
          aria-hidden="true"
          onClick={onClose}
          className={`fixed inset-0 z-40 bg-black/50 transition-opacity duration-300 ${
            isOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
          }`}
        />
      )}

      {/* PAINEL DO DRAWER */}
      <div
        role="presentation"
        className={`bg-white transition-transform duration-300 ease-in-out flex flex-col overflow-y-auto ${anchorClasses} ${
          getElevationClass(elevation)
        } ${
          // O painel fica fixed para temporary/persistent. Para permanent pode ser ajustado conforme o layout da aplicação.
          isPermanent ? "relative" : "fixed z-50"
        } ${
          isOpen ? transformOpenClasses : transformClosedClasses
        } ${className}`}
        {...props}
      >
        {children}
      </div>
    </>
  );
}